import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, CircularProgress, Button, IconButton, TextField, Checkbox, FormControlLabel } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit'; // Import the edit icon
import { toast } from 'react-toastify';

const TerritoriesAndDefaultQuotesModal = ({ open, onClose, selectedAgency }) => {
  const [countyData, setCountyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setEditModalOpen] = useState(false); // State for edit modal
  const [selectedRow, setSelectedRow] = useState(null); // State for the selected row to edit

  useEffect(() => {
    if (open && selectedAgency) {
      fetchCountyData();
    }
  }, [open, selectedAgency]);

  // Function to fetch data
  const fetchCountyData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;

      const response = await fetch(`${baseURL}Location/countiesByAgencies/${selectedAgency.id}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch counties data');
      }

      const data = await response.json();
      setCountyData(data);
    } catch (error) {
      setError(error.message);
      toast.error('Error fetching county data');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle edit click and open the modal
  const handleEditClick = (row) => {
    setSelectedRow(row); // Set the selected row data
    setEditModalOpen(true); // Open the edit modal
  };

  const handleEditModalClose = () => {
    setSelectedRow(null); // Clear the selected row data
    setEditModalOpen(false); // Close the edit modal
  };

  // Function to handle the save action
  const handleSave = async () => {
    if (!selectedRow) return;

    const token = localStorage.getItem('jwtToken');
    const baseURL = process.env.REACT_APP_BASE_URL;
    
    const payload = {
      tbondExecutionFeeQuote: selectedRow.tbondExecutionFeeQuote || null,
      tbondMisQuote: selectedRow.tbondMisQuote || null,
      tbondTravelQuote: selectedRow.tbondTravelQuote || null,
      isPreferred: selectedRow.isPreferred || false,
      quoteDuration: selectedRow.quoteDuration ? selectedRow.quoteDuration : null, // Convert hours to minutes
      timeToPost: selectedRow.timeToPost ? selectedRow.timeToPost : null, // Convert hours to minutes
    };

    try {
      const response = await fetch(`${baseURL}Location/countiesByAgencies/${selectedRow.agentCountyID}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to update county data');
      }

      toast.success('County data updated successfully');
      handleEditModalClose(); // Close the edit modal
      fetchCountyData(); // Refresh the data grid
    } catch (error) {
      toast.error('Failed to save the changes');
    }
  };

  const columns = [
    { field: 'state', headerName: 'State', width: 50 },
    { field: 'county', headerName: 'County', width: 150 },
    { field: 'tbondExecutionFeeQuote', headerName: 'Execution Fee', width: 100, valueGetter: (params) => params || '-' },
    { field: 'tbondMisQuote', headerName: 'Misc Fee', width: 100, valueGetter: (params) => params || '-' },
    { field: 'tbondTravelQuote', headerName: 'Travel Fee', width: 100, valueGetter: (params) => params || '-' },
    {
      field: 'quoteDuration',
      headerName: 'Quote Duration (hrs)',
      width: 150,
      valueGetter: (params) => (params ? (params / 60).toFixed(2) : '-'), // Display in hours
    },
    {
      field: 'timeToPost',
      headerName: 'Time to Post (hrs)',
      width: 150,
      valueGetter: (params) => (params ? (params / 60).toFixed(2) : '-'), // Display in hours
    },
    {
      field: 'isPreferred',
      headerName: 'Preferred',
      width: 150,
      valueGetter: (params) => (params === null ? '-' : (params ? 'Yes' : 'No')),
    },
    {
      field: 'edit',
      headerName: 'Edit',
      width: 100,
      renderCell: (params) => (
        <IconButton color="primary" onClick={() => handleEditClick(params.row)}>
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="territories-default-quotes-modal"
        aria-describedby="territories-default-quotes-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '70%', md: '75%' },
            maxHeight: '85vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            overflowY: 'auto',
          }}
        >
          {/* Header with light gray background */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, bgcolor: '#f9f9f9', p: 2 }}>
            <Typography variant="h5" sx={{ textAlign: 'center', flexGrow: 1 }}>
              Territories & Default Quotes
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Agent Name */}
          <Typography variant="subtitle1" sx={{ textAlign: 'center', mb: 2 }}>
            {selectedAgency && `${selectedAgency.label} `}
          </Typography>

          {/* Description Bullet Point */}
          <Typography variant="body2" sx={{ mb: 2 }}>
            • This page allows you to view and edit your default quotes and territory settings for each county.
          </Typography>

          {loading ? (
            <CircularProgress />
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : countyData.length === 0 ? (
            <Typography>No data available</Typography>
          ) : (
            <div style={{ height: 400, width: '100%' }}>
              <DataGrid
                rows={countyData}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 }, // Set initial page size to 10
                  },
                }}
                pageSizeOptions={[10]}
                pagination // Limit rows per page options to only 10
                columns={columns}
                autoHeight
                disableSelectionOnClick
                sx={{ fontSize: '0.85rem' }}
                getRowId={(row) => row.agentCountyID}
              />
            </div>
          )}
        </Box>
      </Modal>

      {/* Edit Modal for Editing Fees */}
      {selectedRow && (
        <Modal
          open={isEditModalOpen}
          onClose={handleEditModalClose}
          aria-labelledby="edit-county-modal"
          aria-describedby="edit-county-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: '50%', md: '40%' },
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Edit Fees for {selectedRow.county}, {selectedRow.state}
            </Typography>
            {/* Input fields for fees */}
            <TextField
              label="Misc Fee"
              value={selectedRow.tbondMisQuote || ''}
              onChange={(e) => setSelectedRow({ ...selectedRow, tbondMisQuote: e.target.value.replace(/\D/g, '') })}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Travel Fee"
              value={selectedRow.tbondTravelQuote || ''}
              onChange={(e) => setSelectedRow({ ...selectedRow, tbondTravelQuote: e.target.value.replace(/\D/g, '') })}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Quote Duration (hrs)"
              value={selectedRow.quoteDuration ? (selectedRow.quoteDuration / 60) : ''}
              onChange={(e) => setSelectedRow({ ...selectedRow, quoteDuration: e.target.value ? e.target.value * 60 : null })} // Store in minutes
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Time to Post (hrs)"
              value={selectedRow.timeToPost ? (selectedRow.timeToPost / 60) : ''}
              onChange={(e) => setSelectedRow({ ...selectedRow, timeToPost: e.target.value ? e.target.value * 60 : null })} // Store in minutes
              fullWidth
              sx={{ mb: 2 }}
            />

            {/* Checkbox for isPreferred */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedRow.isPreferred || false}
                  onChange={(e) => setSelectedRow({ ...selectedRow, isPreferred: e.target.checked })}
                />
              }
              label="Preferred"
            />

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Save
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleEditModalClose}>
                Cancel
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default TerritoriesAndDefaultQuotesModal;
